import React, { useEffect, useState } from 'react';
import { Field, Form as FormikForm, Formik, getIn } from 'formik';
import { Button } from 'lmc-ui-library';
import { Fields, Tabs } from 'modules/af_cm_ui_kit/components';
import {
  ApplicantDetails,
  CurrentIncome,
  CurrentPension,
  FutureIncome,
  FuturePension,
  LifeEvents,
  LoanDetails,
  ResultSection,
} from '../../partials';

import { ApplicantKey, APPLICANTS_TYPES, FIELDS, Props, FormValues } from './Form.types';
import styles from './Form.module.scss';
import mixins from '../../../../styles/index.module.scss';
import { Option } from 'modules/af_cm_ui_kit/components/Switch/Switch.types';
import { Option as GlobalOption } from 'shared/interface/globalInterface';
import validationSchema from './utils/validationSchema';
import Liabilities from '../Liabilities/Liabilities';
import ErrorManager from 'shared/Messages/ErrorManager';
import { ProductRepaymentType, ProductType } from 'domain/Product';

const applicantsOptions: Option[] = [
  { label: 'Single', value: APPLICANTS_TYPES.SINGLE },
  { label: 'Joint', value: APPLICANTS_TYPES.JOINT },
];

const numberOfApplicantsToTypeMap = new Map<number, APPLICANTS_TYPES>([
  [1, APPLICANTS_TYPES.SINGLE],
  [2, APPLICANTS_TYPES.JOINT],
]);

const applicantTypeToNumberofApplicants = new Map<APPLICANTS_TYPES, number>([
  [APPLICANTS_TYPES.SINGLE, 1],
  [APPLICANTS_TYPES.JOINT, 2],
]);

const getApplicantsKeys = (numberOfApplicants: number): ApplicantKey[] => {
  return ['applicant1', 'applicant2'].splice(0, numberOfApplicants) as ApplicantKey[];
};

export const getIsRetired = (values: FormValues, applicantPrefix: string) =>
  getIn(values, `${applicantPrefix}.${FIELDS.APPLICANT_LIFE_STAGE}`) === 'RETIRED'

const Form: React.FC<Props> = ({
  onSubmit,
  initialValues: formValues,
  results: resultsViewModel,
  resources,
  onExportClick,
  error,
}) => {
  const [productType, setProductType] = useState<ProductType | undefined>();

  const getSelectedNumberOfApplicants = (numberOfApplicants: number) => {
    return numberOfApplicantsToTypeMap.get(numberOfApplicants);
  };

  const getApplicantNameHandler = (applicantKey: string) => {
    return (name: FIELDS) => `${applicantKey}.${name}`;
  };

  const hasApplicantErrorHandler = (applicantKey: ApplicantKey, errors: any, touched: any) => {
    return (field: FIELDS) =>
      errors &&
      touched &&
      errors[applicantKey] &&
      touched[applicantKey] &&
      errors[applicantKey][field] &&
      touched[applicantKey][field];
  };

  const handleProductChange = (option: GlobalOption<{ productType: ProductType | undefined }>) => {
    setProductType(option.params?.productType);
  };

  useEffect(() => {
    if (
      formValues &&
      formValues[FIELDS.LOAN_DETAILS_PREFIX] &&
      formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.MORTGAGE_PRODUCT]
    ) {
      const findProduct = resources.products.filter(
        (product) => product.value === formValues[FIELDS.LOAN_DETAILS_PREFIX][FIELDS.MORTGAGE_PRODUCT],
      );
      if (findProduct.length === 1) {
        const product = findProduct[0] as GlobalOption<{ productType: ProductType | undefined }>;
        setProductType(product.params?.productType);
      }
    }
  }, [formValues, resources.products]);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={formValues}
      validationSchema={validationSchema(
        (resources.products as any) as GlobalOption<{
          productType: string;
          code: string;
          availableRepaymentTypes: ProductRepaymentType[];
        }>[],
        productType,
        null,
      )}
      enableReinitialize>
      {({ values, errors, touched, setFieldValue }) => (
        <FormikForm className={styles.form}>
          <div>
            <div className={styles.box}>
              <section>
                <h1>Application type</h1>
                <h5>This adds or removes a second applicant from the calculation</h5>
                <div className={mixins.space}>
                  <Field
                    component={Fields.Switch}
                    title={''}
                    name={FIELDS.NUMBER_OF_APPLICANTS}
                    options={applicantsOptions.map((applicantOption) => ({
                      label: applicantOption.label,
                      value: applicantOption.value,
                    }))}
                    onChange={(option: Option) =>
                      setFieldValue(
                        FIELDS.NUMBER_OF_APPLICANTS,
                        applicantTypeToNumberofApplicants.get(option.value as APPLICANTS_TYPES),
                      )
                    }
                    selected={getSelectedNumberOfApplicants(values[FIELDS.NUMBER_OF_APPLICANTS])}
                  />
                </div>
              </section>

              <section>
                <h1>Loan details</h1>
                <h5>This influences the monthly interest rate, LTV, and income buffer</h5>
                <div className={mixins.space}>
                  <LoanDetails
                    products={resources.products}
                    termLengths={resources.termLengths}
                    productType={productType}
                    handleProductChange={handleProductChange}
                    values={values}
                  />
                </div>
              </section>
            </div>

            {getApplicantsKeys(values.numberOfApplicants).map((applicantKey, index) => {
              const getApplicantName = getApplicantNameHandler(applicantKey);
              const hasApplicantError = hasApplicantErrorHandler(applicantKey, errors, touched);

              return (
                <div className={styles.box} key={`applicant-${index}`}>
                  <section>
                    <h1>Applicant {index + 1}</h1>
                    <h5>The age the applicant plans to retire heavily influences the rest of the calculation</h5>
                    <div className={mixins.space}>
                      <ApplicantDetails
                        prefix={applicantKey}
                        genders={resources.gender}
                        lifeStages={resources.lifeStage}
                        maritalStatus={resources.maritalStatus}
                      />
                    </div>
                  </section>

                  <section>
                    <h1>Income</h1>
                    <h5>Income is assumed to end at retirement age unless anticipated to continue</h5>
                    <div className={mixins.space}>
                      <Tabs
                        labels={[
                          {
                            value: 'Current',
                            hasError: hasApplicantError(FIELDS.CURRENT_INCOME_PREFIX),
                          },
                          {
                            value: 'Future',
                            hasError: hasApplicantError(FIELDS.FUTURE_INCOME_PREFIX),
                          },
                        ]}>
                        <CurrentIncome
                          prefix={getApplicantName(FIELDS.CURRENT_INCOME_PREFIX)}
                          buisinessTypes={resources.businessType}
                        />
                        <FutureIncome prefix={getApplicantName(FIELDS.FUTURE_INCOME_PREFIX)} />
                      </Tabs>
                    </div>
                  </section>

                  <section>
                    <h1>Pensions</h1>
                    <h5>
                      You can mix current and anticipated private pensions, but state pensions are binary and only
                      require one number in either current or anticipated – leave the remaining field blank.
                    </h5>
                    <div className={mixins.space}>
                      <Tabs
                        labels={[
                          {
                            value: 'Current',
                            hasError: hasApplicantError(FIELDS.CURRENT_PENSION_PREFIX),
                          },
                          {
                            value: 'Future',
                            hasError: hasApplicantError(FIELDS.FUTURE_PENSION_PREFIX),
                          },
                        ]}>
                        <CurrentPension prefix={getApplicantName(FIELDS.CURRENT_PENSION_PREFIX)} />
                        <FuturePension
                          prefix={getApplicantName(FIELDS.FUTURE_PENSION_PREFIX)}
                          useOfFunds={resources.useOfFunds}
                        />
                      </Tabs>
                    </div>
                  </section>
                  <section>
                    <h1>Liabilities</h1>
                    <h5>We require a monthly repayment number except for credit cards</h5>
                    <div className={mixins.space}>
                      <Liabilities
                        creditCommitments={resources.liabilities}
                        prefix={getApplicantName(FIELDS.LIABILITY_PREFIX)}
                      />
                    </div>
                  </section>

                  <section>
                    <h1>Life events</h1>
                    <h5>Life assurance, mortgage protection and sale of assets</h5>
                    <div className={mixins.space}>
                      <LifeEvents prefix={getApplicantName(FIELDS.LIFE_EVENTS_PREFIX)} />
                    </div>
                  </section>
                </div>
              );
            })}
          </div>
          <aside>
            {resultsViewModel && (
              <div className={mixins.sticky}>
                <ResultSection title="Loan details" results={resultsViewModel.loanDetails} />
                <ResultSection results={resultsViewModel.amounts} />
                <div className={styles.actions}>
                  <Button mode="secondary" type="submit">
                    Calculate
                  </Button>
                  <Button mode="ghost" onClick={onExportClick.bind(null, 'csv')}>
                    Export to .csv
                  </Button>
                </div>
                {error && <ErrorManager error={error} />}
              </div>
            )}
          </aside>
        </FormikForm>
      )}
    </Formik>
  );
};

export default Form;
